import React, { useState } from 'react';
import { Paperclip } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DetailSection from '@components/common/DetailSection';
import Button from '@components/common/Button';
import ConfirmationModal from '@components/common/modals/ConfirmationModal';
import { attachmentService } from '@services/domain/attachmentService';
import { showToast } from '@utils/toast';
import { getApiErrorMessage } from '@utils/apiError';
import type { AttachmentEntityType, EntityAttachment } from '@app-types/attachment';
import AttachmentList from './AttachmentList';
import AttachmentUploadModal from './AttachmentUploadModal';
import styles from './AttachmentSection.module.css';

interface AttachmentSectionProps {
  entityType: AttachmentEntityType;
  entityId: number;
  canManage: boolean;
}

const AttachmentSection: React.FC<AttachmentSectionProps> = ({
  entityType,
  entityId,
  canManage,
}) => {
  const queryClient = useQueryClient();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<EntityAttachment | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['attachments', entityType, entityId],
    queryFn: () => attachmentService.listAttachments(entityType, entityId),
    enabled: !!entityId,
  });

  const uploadMutation = useMutation({
    mutationFn: (files: File[]) =>
      attachmentService.uploadAttachments(entityType, entityId, files),
    onSuccess: (result) => {
      showToast.success(result.message);
      setIsUploadModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['attachments', entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: ['audit-history', entityType, entityId] });
    },
    onError: (error: unknown) => {
      showToast.error(getApiErrorMessage(error, 'Failed to upload files'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (attachmentId: number) =>
      attachmentService.deleteAttachment(entityType, entityId, attachmentId),
    onSuccess: () => {
      showToast.success('Attachment deleted successfully');
      setAttachmentToDelete(null);
      queryClient.invalidateQueries({ queryKey: ['attachments', entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: ['audit-history', entityType, entityId] });
    },
    onError: (error: unknown) => {
      showToast.error(getApiErrorMessage(error, 'Failed to delete attachment'));
    },
  });

  const handleDownload = async (attachment: EntityAttachment) => {
    try {
      if (attachment.downloadUrl) {
        window.open(attachment.downloadUrl, '_blank');
      } else {
        const url = await attachmentService.getDownloadUrl(entityType, entityId, attachment.id);
        window.open(url, '_blank');
      }
    } catch {
      showToast.error('Failed to get download URL');
    }
  };

  const handleDeleteConfirm = () => {
    if (attachmentToDelete) {
      deleteMutation.mutate(attachmentToDelete.id);
    }
  };

  const attachments = data?.attachments ?? [];

  return (
    <DetailSection title="Attachments">
      <div className={styles.sectionContent}>
        <div className={styles.sectionHeader}>
          <span className={styles.fileCount}>
            <Paperclip size={16} />
            {attachments.length} file{attachments.length !== 1 ? 's' : ''}
          </span>
          {canManage && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsUploadModalOpen(true)}
            >
              Attach Files
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className={styles.loading}>Loading attachments...</div>
        ) : (
          <AttachmentList
            attachments={attachments}
            canManage={canManage}
            onDownload={handleDownload}
            onDelete={setAttachmentToDelete}
          />
        )}
      </div>

      <AttachmentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={(files) => uploadMutation.mutate(files)}
        isUploading={uploadMutation.isPending}
      />

      <ConfirmationModal
        isOpen={!!attachmentToDelete}
        onClose={() => setAttachmentToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Attachment"
        message={`Are you sure you want to delete "${attachmentToDelete?.originalName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="danger"
        isProcessing={deleteMutation.isPending}
      />
    </DetailSection>
  );
};

export default AttachmentSection;
